import type { BlendMode } from './types';
import { clamp01, lerp } from './math/scalar';

/** Layer compositing for the height field. All operands are normalised `0 .. 1`. */

export const BLEND_MODES: ReadonlyArray<{ value: BlendMode; label: string }> = [
  { value: 'replace', label: 'Replace' },
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'max', label: 'Max' },
  { value: 'min', label: 'Min' },
  { value: 'average', label: 'Average' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'difference', label: 'Difference' },
];

function combine(base: number, layer: number, mode: BlendMode): number {
  switch (mode) {
    case 'add':
      return base + layer;
    case 'subtract':
      return base - layer;
    case 'multiply':
      return base * layer;
    case 'max':
      return Math.max(base, layer);
    case 'min':
      return Math.min(base, layer);
    case 'average':
      return (base + layer) * 0.5;
    case 'overlay':
      return base < 0.5 ? 2 * base * layer : 1 - 2 * (1 - base) * (1 - layer);
    case 'difference':
      return Math.abs(base - layer);
    case 'replace':
    default:
      return layer;
  }
}

/**
 * Blends one layer onto the accumulated field.
 *
 * `amount` mixes between the untouched base and the blended result, so a layer
 * can be dialled back without changing its mode.
 */
export function blendHeight(base: number, layer: number, mode: BlendMode, amount: number): number {
  const blended = clamp01(combine(base, clamp01(layer), mode));
  return amount >= 1 ? blended : clamp01(lerp(base, blended, clamp01(amount)));
}
